import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { FiCompass, FiCpu, FiZap, FiUsers, FiTrendingUp, FiLayers } from 'react-icons/fi';

const ServicesSection = styled.section`
  background-color: ${props => props.theme.colors.background};
  position: relative;
  padding: 4rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: 1rem;

  .highlight {
    color: ${props => props.theme.colors.primary};
  }
`;

const SectionSubtitle = styled.p`
  color: ${props => props.theme.colors.gray};
  max-width: 640px;
  margin: 0 auto 4rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: ${props => props.theme.colors.dark};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 2.5rem 2rem;
  text-align: left;
  transition: border-color 0.3s ease, transform 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-4px);
  }

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: ${props => props.theme.colors.primary}18;
    color: ${props => props.theme.colors.primary};
    font-size: 1.6rem;
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.35rem;
    margin-bottom: 1rem;
  }

  p {
    color: ${props => props.theme.colors.gray};
    font-size: 1rem;
    line-height: 1.7;
  }
`;

const services = [
  {
    title: 'AI Strategy & Readiness',
    description:
      'Assess where AI genuinely creates value in your business, identify quick wins, and build a pragmatic adoption roadmap — grounded in your real workflows, not hype.',
    icon: <FiCompass />,
  },
  {
    title: 'AI Agent Integration',
    description:
      'Design and deploy AI agents that plug into your existing systems and processes — handling support, operations, research, and internal workflows autonomously.',
    icon: <FiCpu />,
  },
  {
    title: 'AI-Powered Automation',
    description:
      'Turn repetitive, manual processes into intelligent automated workflows using modern AI tooling — reducing cost and freeing your team for higher-value work.',
    icon: <FiZap />,
  },
  {
    title: 'Team Enablement & Training',
    description:
      'Train your teams to work effectively with AI tools and assistants, establishing practices and guardrails so productivity gains actually stick.',
    icon: <FiUsers />,
  },
  {
    title: 'Custom AI Solutions',
    description:
      'From prototype to production: hands-on delivery of AI-powered products and internal tools, built to enterprise standards of reliability and security.',
    icon: <FiLayers />,
  },
  {
    title: 'Engineering Leadership',
    description:
      'Fractional technical leadership for teams adopting AI — aligning engineering strategy, delivery, and organizational change around your AI initiatives.',
    icon: <FiTrendingUp />,
  },
];

const Services = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
      },
    },
  };

  return (
    <ServicesSection id="services">
      <Container>
        <SectionTitle>
          What I <span className="highlight">Do</span>
        </SectionTitle>
        <SectionSubtitle>
          I help businesses adopt AI with confidence — from strategy through hands-on
          integration of AI tools and agents into everyday operations.
        </SectionSubtitle>
        <ServicesGrid
          ref={ref}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {services.map((service, index) => (
            <ServiceCard key={index} variants={cardVariants}>
              <div className="icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services;
